import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Lock } from 'lucide-react';
import JSZip from 'jszip';
import * as pdfjsLib from 'pdfjs-dist';
import { MembershipTier } from '../types/membership';
import { ProcessedBookResult, ProcessedBookChapter } from '../types/content';
import { processBook, getChapterContent } from '../utils/bookProcessor';
import { TierBadge } from './TierBadge';

// Configure pdf.js worker for Vite bundler
// Import the worker as a URL blob for pdf.js
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl as string;

interface FileImportProps {
  onFileLoaded?: (book: ProcessedBookResult) => void;
  onEpubChaptersLoaded?: (chapters: ProcessedBookChapter[]) => void;
  onError?: (error: string) => void;
  tier?: MembershipTier;
}

// Default word limit for guest uploads
const GUEST_WORD_LIMIT = 10000;

// ─── EPUB Navigation Types ───────────────────────────────────────────────────

interface EpubChapter {
  title: string;
  contentPath: string;  // Relative path within EPUB
}

// ─── EPUB Navigation Parsers ─────────────────────────────────────────────────

/**
 * Parse EPUB 2 NCX navigation file to extract chapter list
 * Note: NCX content src attributes are relative to the OPF directory, not the NCX file itself.
 * Handles nested navPoint structures recursively.
 */
function parseNcxToc(ncxDoc: Document, ncxHref: string, opfDir: string): EpubChapter[] {
  const chapters: EpubChapter[] = [];

  function processNavPoint(navPoint: Element, parentTitles: string[] = [], depth: number = 0) {
    const label = navPoint.querySelector('navLabel text')?.textContent || '';
    const contentSrc = navPoint.querySelector('content')?.getAttribute('src') || '';
    const currentTitles = [...parentTitles, label.trim()];
    
    console.log('[parseNcxToc] navPoint depth', depth, ':', { label, contentSrc, opfDir });
    
    // Resolve relative to OPF directory, strip fragment identifiers
    const fullPath = opfDir + contentSrc.split('#')[0];
    console.log('[parseNcxToc] resolved path:', fullPath);
    
    if (contentSrc) {
      const title = currentTitles.filter(t => t).join(' — ') || `Section ${chapters.length + 1}`;
      chapters.push({ title, contentPath: fullPath });
    }

    // Process nested navPoints recursively
    const nestedNavPoints = navPoint.querySelectorAll(':scope > navPoint');
    if (nestedNavPoints.length === 0) {
      // Also try direct children without the :scope prefix
      const children = navPoint.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.tagName.toLowerCase() === 'navpoint') {
          processNavPoint(child, currentTitles, depth + 1);
        }
      }
    } else {
      nestedNavPoints.forEach(np => processNavPoint(np, currentTitles, depth + 1));
    }
  }

  const topLevelNavPoints = ncxDoc.querySelectorAll(':root > navMap > navPoint, navMap > navPoint');
  topLevelNavPoints.forEach(navPoint => processNavPoint(navPoint, [], 0));

  // Fallback: if no navPoints found with the above selectors, try all navPoints
  if (chapters.length === 0) {
    const allNavPoints = ncxDoc.querySelectorAll('navPoint');
    const processed = new Set();
    allNavPoints.forEach(navPoint => {
      // Only process top-level navPoints (those not nested inside another navPoint)
      let parent = navPoint.parentElement;
      let isNested = false;
      while (parent) {
        if (parent.tagName.toLowerCase() === 'navpoint') {
          isNested = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (!isNested && !processed.has(navPoint)) {
        processNavPoint(navPoint, [], 0);
        processed.add(navPoint);
      }
    });
  }

  return chapters;
}

/**
 * Parse EPUB 3 NavDoc navigation to extract chapter list
 * Note: NavDoc links are relative to the OPF directory, not the NavDoc file itself.
 * Handles nested ol/li structures recursively.
 */
function parseNavDocToc(navElement: Element, navHref: string, opfDir: string): EpubChapter[] {
  const chapters: EpubChapter[] = [];

  function processTocOl(olElement: Element, parentTitles: string[] = [], depth: number = 0) {
    const listItems = olElement.querySelectorAll(':scope > li');
    
    listItems.forEach(li => {
      const link = li.querySelector('a[href]');
      const title = link ? link.textContent?.trim() : '';
      const href = link ? link.getAttribute('href') || '' : '';
      const currentTitles = [...parentTitles, title];

      if (href && title) {
        const fullPath = opfDir + href.split('#')[0];
        const chapterTitle = currentTitles.filter(t => t).join(' — ') || `Section ${chapters.length + 1}`;
        console.log('[parseNavDocToc] link depth', depth, ':', { title, href, fullPath });
        chapters.push({ title: chapterTitle, contentPath: fullPath });
      }

      // Process nested ol elements recursively
      const nestedOl = li.querySelector(':scope > ol');
      if (nestedOl) {
        processTocOl(nestedOl, currentTitles, depth + 1);
      }
    });
  }

  // Find all top-level ol elements within the nav element
  const topLevelOls = navElement.querySelectorAll(':scope > ol');
  if (topLevelOls.length > 0) {
    topLevelOls.forEach(ol => processTocOl(ol, [], 0));
  } else {
    // Fallback: process all ol elements
    const allOls = navElement.querySelectorAll('ol');
    allOls.forEach(ol => {
      // Only process if this ol is a direct child or not nested in another li
      const parentLi = ol.parentElement;
      if (!parentLi || parentLi.tagName.toLowerCase() !== 'li') {
        processTocOl(ol, [], 0);
      }
    });
  }

  // If still no chapters found, fall back to flat link extraction
  if (chapters.length === 0) {
    const links = navElement.querySelectorAll('a[href]');
    links.forEach(link => {
      const title = link.textContent || '';
      const href = link.getAttribute('href') || '';
      const fullPath = opfDir + href.split('#')[0];
      if (title.trim() && href) {
        chapters.push({ title: title.trim(), contentPath: fullPath });
      }
    });
  }

  return chapters;
}

/**
 * Fallback: extract chapter list from OPF spine order (no titles, just file paths)
 */
function parseSpineOrder(opfDoc: Document, opfDir: string): EpubChapter[] {
  const chapters: EpubChapter[] = [];
  const spine = opfDoc.querySelector('spine');
  const manifest = opfDoc.querySelector('manifest');
  if (!spine || !manifest) return chapters;

  const items = manifest.querySelectorAll('item');
  const itemRefs = spine.querySelectorAll('itemref');

  console.log('[parseSpineOrder] opfDir:', opfDir, 'itemRefs count:', itemRefs.length);

  itemRefs.forEach((itemref, i) => {
    const idref = itemref.getAttribute('idref');
    const manifestItem = Array.from(items).find(
      item => item.getAttribute('id') === idref
    );
    const href = manifestItem?.getAttribute('href') || '';
    const mediaType = manifestItem?.getAttribute('media-type') || '';

    console.log('[parseSpineOrder] itemref:', { idref, href, mediaType });

    // Only include XHTML/HTML items (skip CSS, images, etc.)
    if (mediaType === 'application/xhtml+xml' || mediaType === 'text/html') {
      const contentPath = opfDir + href.split('#')[0];
      console.log('[parseSpineOrder] contentPath:', contentPath);
      chapters.push({
        title: `Section ${i + 1}`,
        contentPath,
      });
    }
  });

  return chapters;
}

/**
 * Main navigation parser — tries EPUB 3 NavDoc, then EPUB 2 NCX, then spine fallback
 */
async function parseEpubNavigation(
  zip: JSZip,
  opfPath: string,
  opfDoc: Document
): Promise<EpubChapter[]> {
  const opfDir = opfPath.includes('/') ? opfPath.substring(0, opfPath.lastIndexOf('/') + 1) : '';
  console.log('[parseEpubNavigation] opfPath:', opfPath, 'opfDir:', opfDir);

  // Try EPUB 3 NavDoc first
  const navItem = opfDoc.querySelector('manifest item[media-type="application/xhtml+xml"][properties~="nav"]');
  if (navItem) {
    const navHref = navItem.getAttribute('href');
    console.log('[parseEpubNavigation] Found NavDoc:', navHref);
    if (navHref) {
      const navContent = await zip.file(opfDir + navHref)?.async('text');
      if (navContent) {
        const navDoc = new DOMParser().parseFromString(navContent, 'application/xhtml+xml');
        // Try multiple selector patterns for the TOC nav element
        const tocNav = navDoc.querySelector('nav[*|type="toc"]')
          || navDoc.querySelector('nav[epub|type="toc"]')
          || Array.from(navDoc.querySelectorAll('nav')).find(
            n => n.getAttributeNS('http://www.idpf.org/2007/ops', 'type') === 'toc'
          );
        if (tocNav) {
          console.log('[parseEpubNavigation] Using EPUB 3 NavDoc');
          return parseNavDocToc(tocNav, navHref, opfDir);
        }
      }
    }
  }

  // Fall back to EPUB 2 NCX
  const ncxItem = opfDoc.querySelector('manifest item[media-type="application/x-dtbncx+xml"]');
  if (ncxItem) {
    const ncxHref = ncxItem.getAttribute('href');
    console.log('[parseEpubNavigation] Found NCX:', ncxHref);
    if (ncxHref) {
      const ncxContent = await zip.file(opfDir + ncxHref)?.async('text');
      if (ncxContent) {
        const ncxDoc = new DOMParser().parseFromString(ncxContent, 'text/xml');
        return parseNcxToc(ncxDoc, ncxHref, opfDir);
      }
    }
  } else {
    console.log('[parseEpubNavigation] No NCX item found, trying spine fallback');
  }

  // Final fallback: use spine order
  console.log('[parseEpubNavigation] Using spine order fallback');
  return parseSpineOrder(opfDoc, opfDir);
}

/**
 * Extract text content from a single EPUB chapter file
 */
async function extractChapterContent(
  zip: JSZip,
  chapter: EpubChapter,
): Promise<{ title: string; content: string }> {
  const zipEntry = zip.file(chapter.contentPath);
  if (!zipEntry) {
    throw new Error(`Chapter file not found in EPUB: ${chapter.contentPath}`);
  }

  const content = await zipEntry.async('text');
  const doc = new DOMParser().parseFromString(content, 'text/html');
  const text = doc.body?.textContent || '';

  // Clean up whitespace
  const cleaned = text
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();

  return { title: chapter.title || 'Untitled', content: cleaned };
}

// ─── File Processors ─────────────────────────────────────────────────────────

const processTxtFile = async (file: File): Promise<ProcessedBookResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const result = processBook(content, file.name);
        
        if (!result.success || !result.book) {
          reject(new Error(result.error || 'Failed to process text file'));
          return;
        }
        
        const { book } = result;
        const chapters = book.chapters.map(ch => ({
          id: ch.id,
          title: ch.title,
          content: getChapterContent(book, ch.id),
          wordCount: ch.wordCount
        }));
        
        resolve({
          title: book.title,
          chapters,
          fullText: book.cleanText,
          totalWords: book.totalWords,
          source: 'txt',
          warnings: result.warnings
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

const processEpubFile = async (file: File): Promise<ProcessedBookResult> => {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Find container.xml to locate OPF
  const containerXml = await zip.file('META-INF/container.xml')?.async('text');
  if (!containerXml) {
    throw new Error('Invalid EPUB structure: missing container.xml');
  }

  const parser = new DOMParser();
  const containerDoc = parser.parseFromString(containerXml, 'text/xml');
  const rootfile = containerDoc.querySelector('rootfile');
  const opfPath = rootfile?.getAttribute('full-path') || 'content.opf';

  // Parse OPF manifest
  const opfXml = await zip.file(opfPath)?.async('text');
  if (!opfXml) {
    throw new Error('Cannot find content manifest');
  }

  const opfDoc = parser.parseFromString(opfXml, 'text/xml');
  const opfDir = opfPath.includes('/') ? opfPath.substring(0, opfPath.lastIndexOf('/') + 1) : '';
  console.log('[processEpubFile] opfPath:', opfPath, 'opfDir:', opfDir);

  // Log manifest items for debugging
  const manifestItems = opfDoc.querySelectorAll('manifest item');
  console.log('[processEpubFile] Manifest items:');
  manifestItems.forEach(item => {
    console.log('  - id:', item.getAttribute('id'), 'href:', item.getAttribute('href'), 'media-type:', item.getAttribute('media-type'));
  });

  // Extract title from OPF metadata
  const metadata = opfDoc.querySelector('metadata');
  const titleElement = metadata?.querySelector('dc\\:title, title');
  const bookTitle = titleElement?.textContent || file.name.replace(/\.[^/.]+$/, '');

  // Parse navigation structure
  const chapters = await parseEpubNavigation(zip, opfPath, opfDoc);

  if (chapters.length === 0) {
    throw new Error('No chapters found in EPUB file');
  }

   // Extract content for each chapter
   const chapterContents = await Promise.all(
     chapters.map(async (ch, i) => {
       const { title, content } = await extractChapterContent(zip, ch);
       const wordCount = content.split(/\s+/).filter(w => w.length > 0).length;
       
       // Log warning if chapter content is empty (but continue processing)
       if (content.trim().length === 0) {
         console.warn(`[FileImport] Chapter "${ch.title}" (path: ${ch.contentPath}) extracted with empty content`);
       }
       
       return {
         id: `chapter-${i + 1}`,
         title: title || `Chapter ${i + 1}`,
         content,
         wordCount,
       };
     })
   );

  const fullText = chapterContents.map(ch => ch.content).join('\n\n');
  const totalWords = chapterContents.reduce((sum, ch) => sum + ch.wordCount, 0);

  const warnings: string[] = [];
  if (chapters.length === 1) {
    warnings.push('Only one section found — chapter navigation may not be available in this EPUB');
  }

  return {
    title: bookTitle,
    chapters: chapterContents,
    fullText,
    totalWords,
    source: 'epub',
    warnings: warnings.length > 0 ? warnings : undefined,
  };
};

// ─── PDF Processor ────────────────────────────────────────────────────────────

/**
* Process a PDF file using pdf.js, then use bookProcessor for chapter detection
* Note: PDF text extraction works best with text-based PDFs; scanned images will fail
*/
const processPdfFile = async (file: File): Promise<ProcessedBookResult> => {
 console.log('[processPdfFile] Starting PDF processing for:', file.name, 'size:', file.size);

 // Use a timeout to prevent infinite hangs
 const timeoutPromise = new Promise<never>((_, reject) => {
   setTimeout(() => reject(new Error('PDF processing timed out after 30 seconds')), 30000);
 });

 try {
   const arrayBuffer = await file.arrayBuffer();
   console.log('[processPdfFile] ArrayBuffer loaded, size:', arrayBuffer.byteLength);

   // Race the PDF loading against a timeout
   const pdf = await Promise.race([
     pdfjsLib.getDocument({ data: arrayBuffer }),
     timeoutPromise
   ]) as any;
   console.log('[processPdfFile] PDF loaded, numPages:', pdf.numPages);

   // Process pages with a per-page timeout to avoid hanging on a single page
   let fullText = '';
   for (let i = 1; i <= pdf.numPages; i++) {
     console.log('[processPdfFile] Processing page', i, 'of', pdf.numPages);
     const page = await pdf.getPage(i);
     const textContent = await page.getTextContent();
     const pageText = textContent.items.map((item: any) => item.str).join(' ');
     fullText += pageText + '\n\n';
     console.log('[processPdfFile] Page', i, 'extracted, text length:', pageText.length);
   }
   console.log('[processPdfFile] Total text extracted:', fullText.length, 'chars');

   // Use bookProcessor for chapter detection (regex-based)
   const result = processBook(fullText, file.name);

   if (result.success && result.book) {
     const { book } = result;
     const chapters = book.chapters.map(ch => ({
       id: ch.id,
       title: ch.title,
       content: getChapterContent(book, ch.id),
       wordCount: ch.wordCount
     }));

     return {
       title: book.title,
       chapters,
       fullText: book.cleanText,
       totalWords: book.totalWords,
       source: 'pdf',
       warnings: result.warnings
     };
   }

   // Fallback: no chapters detected
   return {
     title: file.name.replace(/\.[^/.]+$/, ''),
     chapters: [{
       id: '1',
       title: 'Full Document',
       content: fullText,
       wordCount: fullText.split(/\s+/).filter(w => w.length > 0).length
     }],
     fullText,
     totalWords: fullText.split(/\s+/).filter(w => w.length > 0).length,
     source: 'pdf',
     warnings: ['No chapters detected — treating as single document']
   };
 } catch (error) {
   console.error('[processPdfFile] Error:', error);
   throw error;
 }
};

// ─── Component ───────────────────────────────────────────────────────────────

export const FileImport: React.FC<FileImportProps> = ({
  onFileLoaded,
  onEpubChaptersLoaded,
  onError,
  tier = 'guest' as MembershipTier,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Tier gating for uploads
  const canUploadTxt = tier !== 'guest'; // Starter+ can upload .txt
  const canUploadEpub = tier === 'reader' || tier === 'ultra'; // Reader+ can upload .epub
  const canUploadPdf = tier === 'reader' || tier === 'ultra'; // Reader+ can upload .pdf

  // Determine accepted file types based on tier
  const getAcceptedTypes = () => {
    const types: Record<string, string[]> = {};
    if (canUploadTxt) {
      types['text/plain'] = ['.txt'];
    }
    if (canUploadEpub) {
      types['application/epub+zip'] = ['.epub'];
    }
    if (canUploadPdf) {
      types['application/pdf'] = ['.pdf'];
    }
    return types;
  };

  // Build tier restriction message
  const getTierRestrictionMessage = () => {
    if (tier === 'guest') {
      return 'Upload requires Starter tier or higher';
    }
    if (tier === 'starter') {
      return '.epub upload requires Reader tier or higher';
    }
    return '';
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Early validation: check for empty file
    if (file.size === 0) {
      onError?.(`File "${file.name}" is empty (0 bytes). Please select a non-empty file.`);
      return;
    }

    setIsProcessing(true);

    try {
      let book: ProcessedBookResult;

      if (file.type === 'application/epub+zip' || file.name.endsWith('.epub')) {
        book = await processEpubFile(file);
        // Call the new callback with just the chapters for the selection modal
        onEpubChaptersLoaded?.(book.chapters);
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        // PDF processing — uses pdf.js for text extraction + bookProcessor for chapter detection
        book = await processPdfFile(file);
      } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        // TXT processing — uses bookProcessor for chapter detection
        book = await processTxtFile(file);

        // Enforce word limit for guest users
        if (tier === 'guest') {
          if (book.totalWords > GUEST_WORD_LIMIT) {
            throw new Error(`File exceeds ${GUEST_WORD_LIMIT.toLocaleString()} word limit (${book.totalWords.toLocaleString()} words). Upgrade to Starter for unlimited uploads.`);
          }
        }
      } else {
        throw new Error('Unsupported file type. Please upload .epub, .pdf, or .txt files.');
      }

      // Debug logging (development only)
      console.log('[FileImport] Processed:', {
        name: file.name,
        size: file.size,
        type: file.type,
        totalWords: book.totalWords,
        fullTextLength: book.fullText.length
      });

      if (!book.fullText.trim()) {
        throw new Error(
          `File "${file.name}" produced no readable text (${file.size} bytes, ${book.totalWords} words). ` +
          `The file may be empty, corrupted, or in an unsupported format.`
        );
      }

      onFileLoaded?.(book);
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  }, [onFileLoaded, onEpubChaptersLoaded, onError, tier]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptedTypes(),
    maxFiles: 1,
    disabled: isProcessing || (!canUploadTxt && !canUploadEpub && !canUploadPdf)
  });

  const restrictionMessage = getTierRestrictionMessage();

  // Compute overall upload ability and UI state
  const canUploadAny = canUploadTxt || canUploadEpub || canUploadPdf;
  const isUploadLocked = isProcessing || !canUploadAny;

  // Determine the message to show in locked state
  const getLockedMessage = () => {
    if (tier === 'guest') {
      return 'Upload requires Starter tier or higher';
    }
    if (tier === 'starter') {
      return '.epub and .pdf uploads require Reader tier or higher';
    }
    return restrictionMessage;
  };

  // Determine supported file types UI with tier badges
  const renderSupportedFileTypes = () => {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 space-y-1">
        <div className="flex items-center gap-2">
          <span>.txt (Starter+)</span>
          {tier === 'guest' && <TierBadge tier="starter" />}
        </div>
        <div className="flex items-center gap-2">
          <span>.epub (Reader+)</span>
          {!canUploadEpub && <TierBadge tier="reader" />}
        </div>
        <div className="flex items-center gap-2">
          <span>.pdf (Reader+)</span>
          {!canUploadPdf && <TierBadge tier="reader" />}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${isDragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'}
          ${isUploadLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <input {...getInputProps()} />
        
        {isProcessing ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Processing file...</p>
          </div>
        ) : !canUploadAny ? (
          <div className="flex flex-col items-center">
            <Lock className="h-8 w-8 text-gray-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Upload locked
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
              {getLockedMessage()}
              <TierBadge tier="starter" />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="h-8 w-8 text-gray-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {isDragActive ? 'Drop file here' : 'Drag & drop or click to upload'}
            </p>
            {renderSupportedFileTypes()}
            {(tier as MembershipTier) === 'guest' && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Limit: {GUEST_WORD_LIMIT.toLocaleString()} words
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
